/**
 * Hyderabad verdict seed data.
 * Hand-seeded 60 neighborhoods. This is Hyperlocal's core loop:
 *   open -> tap -> verdict.
 *
 * Schema is versioned: this is v2 (confidence + tags + sources added).
 * Web and mobile app share this exact file for now — single source of truth.
 *
 * Data is fake-but-plausible. Replace with real signals once verdict pipeline ships.
 */

export type VerdictLevel = "green" | "amber" | "red";

export interface VerdictSource {
  /** Display label, e.g., "GHMC 2024 infra report" or "Resident survey, n=42". */
  label: string;
  /** Optional deep link once real sources exist. */
  url?: string;
}

/**
 * Per-suburb researched pricing. Replaces the heuristic band-derived
 * defaults for suburbs where we have enough evidence to claim a tighter
 * median.
 *
 * Unit conventions (keep consistent with budget-format.ts):
 *   - Melbourne: Buy medians in A$k (so 1200 = A$1.2M); Rent medians in $/wk.
 *   - Hyderabad: Buy medians in ₹ Lakh (so 120 = ₹1.2Cr); Rent medians in ₹k/mo.
 *
 * Every field is optional — seed only what you can defend. The clamp logic
 * falls through to the band range when a field is missing, so partial
 * coverage is safe.
 *
 * `source` should credit a single concrete source per record
 * (realestate.com.au, property.com.au, CoreLogic/Cotality, MagicBricks,
 * 99acres, Sulekha). `year` is the snapshot year for refresh tracking.
 */
export interface SuburbPriceData {
  buyHouseMedian?: number;
  buyApartmentMedian?: number;
  rentHouseMedian?: number;
  rentApartmentMedian?: number;
  source: string;
  year: number;
}

/**
 * Long-term upside signal for the Investor lens. Evidence-based, not a
 * certainty claim — the user sees tiers (stronger / moderate / weaker)
 * with short explanatory drivers + risks, not a score or a promise.
 *
 * `drivers` should be short, specific, and verifiable (infrastructure
 * announcement + year, employer name + expansion, etc). `risks` flag what
 * could break the thesis (oversupply, zoning, market cycle).
 *
 * Absence of this field => no specific investor view; the lens falls back
 * to generic tag-driven scoring.
 */
export interface InvestorEvidence {
  /**
   * Conviction tier on long-run appreciation + rental stability.
   *   stronger  — multiple independent drivers, confirmed capex/jobs
   *   moderate  — one or two drivers, execution-dependent
   *   weaker    — thesis exists but headwinds meaningful
   */
  tier: "stronger" | "moderate" | "weaker";
  /** Why this suburb is a candidate — 2-4 short, specific bullets. */
  drivers: string[];
  /** Why the thesis could break — 1-3 short bullets. */
  risks: string[];
  /**
   * Expected conviction horizon.
   *   short  — 1-2 years (trigger event already priced or imminent)
   *   medium — 3-5 years (infra/jobs rolling in)
   *   long   — 7-10 years (structural growth, speculative tail)
   */
  timeframe: "short" | "medium" | "long";
}

export interface Verdict {
  id: string;
  name: string;
  /** [lng, lat] — MapLibre order. */
  coord: [number, number];
  verdict: VerdictLevel;
  /** Exactly 3 short reasons. One-line each. */
  reasons: [string, string, string];
  /** Ballpark price band, e.g., "₹8k–12k / sqft". */
  priceRange: string;
  /** 0–100. How strong the underlying evidence is. */
  confidenceScore: number;
  /** Freeform classification tags; used later for filters. */
  tags: string[];
  /** Source placeholders. Empty array allowed. */
  sources: VerdictSource[];
  /**
   * Optional: researched median prices — if present, clamp/midpoint logic
   * uses these instead of band defaults. See `SuburbPriceData`.
   */
  priceData?: SuburbPriceData;
  /**
   * Optional: investor-lens evidence. When present, drives stronger/
   * moderate/weaker tiering and surfaces in lens copy. See
   * `InvestorEvidence`.
   */
  investorEvidence?: InvestorEvidence;
}

/** Schema version. Bump when fields change in a non-additive way. */
export const VERDICT_SCHEMA_VERSION = 3;

// `_HYD_RAW` holds the hand-seeded verdict records as-authored. The
// exported `HYD_VERDICTS` below is the same records post-processed to
// back-fill `priceData` and `investorEvidence` from `suburb-pricing.ts`
// for every suburb that did not get those fields inline. Inline fields
// still take precedence via `??`, so the bank is a back-fill not a
// rewrite. This is the architectural commitment — every visible suburb
// resolves to a researched median rather than a generic band fallback.
import { HYD_SUBURB_PRICING, getPricingFor } from "./suburb-pricing";
const _HYD_RAW: Verdict[] = [
  // --- West / IT corridor ---
  {
    id: "gachibowli",
    name: "Gachibowli",
    coord: [78.3489, 17.4401],
    verdict: "green",
    reasons: [
      "15–25 min to HITEC City, 10 min to Wipro Circle off-peak",
      "2BHK rents currently ₹38–55k; up ~8% in last 6 months",
      "Borewell stress Apr–Jun — most towers buy tanker water",
    ],
    priceRange: "₹8k–13k / sqft",
    confidenceScore: 82,
    tags: ["it-corridor", "premium", "young-professionals"],
    sources: [
      { label: "GHMC rental index 2025" },
      { label: "Commute time, Google distance matrix" },
    ],
    priceData: {
      buyApartmentMedian: 135,    // ≈₹1.35Cr — 2BHK median, MagicBricks/99acres Q1 2025
      buyHouseMedian: 250,        // ≈₹2.5Cr — 3BHK gated villa median
      rentApartmentMedian: 42,    // ₹42k/mo — 2BHK mid-floor tower median
      rentHouseMedian: 68,        // ₹68k/mo — 3BHK gated villa median
      source: "99acres + MagicBricks Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Financial District + HITEC anchor demand; Wells Fargo, JPMC, Microsoft all expanding",
        "Rental yield 3.2–3.6% — above HYD west-corridor average",
        "Metro Phase II (Nagole–RGIA via FD) tendered Nov 2024 — closes commute gap",
      ],
      risks: [
        "Apartment oversupply within 2km (12+ towers launched 2023–24)",
        "Water/power stress persists without Krishna supply upgrade",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "madhapur",
    name: "Madhapur (HITEC City)",
    coord: [78.3915, 17.4486],
    verdict: "green",
    reasons: [
      "Walk or 5-min drive to Mindspace, Raheja, Cyber Towers",
      "Resales typically move in 30–45 days; strong broker pipeline",
      "Ayyappa Society junction grinds 6–9pm; avoid west-facing low floors",
    ],
    priceRange: "₹10k–15k / sqft",
    confidenceScore: 88,
    tags: ["it-corridor", "premium", "mature-infra"],
    sources: [
      { label: "Broker sales velocity index" },
      { label: "Traffic pulse — T-HITS 2025" },
    ],
    priceData: {
      buyApartmentMedian: 165,    // 2BHK ₹1.55–1.75Cr range
      buyHouseMedian: 320,
      rentApartmentMedian: 48,
      rentHouseMedian: 80,
      source: "MagicBricks + resident broker panel Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Cyber Towers / Mindspace / Raheja employs 400k+ — weekday demand floor",
        "Land-bank near exhausted — new towers rare; supports price stability",
        "Metro Blue Line operational — one-seat Ameerpet, LB Nagar interchange",
      ],
      risks: [
        "Entry ticket ₹1.5Cr+ — cash-flow yield compressed (2.8–3.2%)",
        "Redevelopment mostly ruled out — legacy stock caps upside",
      ],
      timeframe: "short",
    },
  },
  {
    id: "kondapur",
    name: "Kondapur",
    coord: [78.3654, 17.4647],
    verdict: "green",
    reasons: [
      "~₹3–4k/sqft cheaper than Madhapur, 12 min to HITEC",
      "Botanical Gardens metro + DLF retail within 2 km",
      "Kothaguda nala backs up in heavy rain; check ground-floor units",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 78,
    tags: ["it-corridor", "mid-market", "family-friendly"],
    sources: [
      { label: "HMDA drainage audit 2024" },
    ],
    priceData: {
      buyApartmentMedian: 110,
      buyHouseMedian: 180,
      rentApartmentMedian: 34,
      rentHouseMedian: 55,
      source: "99acres Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Cheapest near-IT pocket with mature schools + hospitals (DPS, CARE)",
        "Botanical Gardens metro delivered 2023 — yield-to-commute best in class",
        "3BHK new launches still under ₹1.3Cr — entry for yield buyers",
      ],
      risks: [
        "Kothaguda nala drainage risk — ground floors discount ~8%",
        "Builder quality variance on 2019–21 stock",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "kukatpally",
    name: "Kukatpally (KPHB)",
    coord: [78.4019, 17.4849],
    verdict: "amber",
    reasons: [
      "50+ schools within 3 km; Forum Mall & Remedy hospital walkable",
      "JNTU–Moosapet long commute: 45–60 min peak, barely moves 7–9pm",
      "NH9 traffic noise; AQI dips Oct–Jan from winter inversion",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 80,
    tags: ["dense", "schools", "traffic-pressure"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "Traffic pulse — T-HITS 2025" },
    ],
    priceData: {
      buyApartmentMedian: 82,
      buyHouseMedian: 140,
      rentApartmentMedian: 28,
      rentHouseMedian: 42,
      source: "MagicBricks Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "School cluster (50+ CBSE/ICSE) drives sticky family tenant base",
        "Forum Mall catchment stable; retail rentals re-priced up in 2024",
      ],
      risks: [
        "Traffic pressure — JNTU/Moosapet commute shows no planned fix",
        "Flat appreciation 2019–24 (4–6%/yr) below HYD west average (9%+)",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "miyapur",
    name: "Miyapur",
    coord: [78.3576, 17.4968],
    verdict: "amber",
    reasons: [
      "Red-line metro terminus — CBD in 45 min, no transfer",
      "₹3–5k/sqft cheaper than HITEC; 2BHK rents still under ₹25k",
      "Industrial belt to the west; AQI 40–60% worse in winter mornings",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 68,
    tags: ["transit-hub", "affordable", "industrial-edge"],
    sources: [
      { label: "Metro daily ridership" },
      { label: "CPCB air data 2024" },
    ],
    priceData: {
      buyApartmentMedian: 65,
      buyHouseMedian: 115,
      rentApartmentMedian: 22,
      rentHouseMedian: 32,
      source: "MagicBricks + 99acres Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Metro Red Line terminus — highest commute certainty in west HYD",
        "Entry ₹65L 2BHK pulls first-time buyers; rental vacancy near zero",
      ],
      risks: [
        "Patancheru industrial edge — AQI drag 40–60% worse in winter",
        "Price appreciation capped by continuous supply from Bachupally / Nizampet",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "nizampet",
    name: "Nizampet",
    coord: [78.3789, 17.5099],
    verdict: "amber",
    reasons: [
      "Young-family pocket; 20+ playschools within 2 km",
      "Builder quality swings block to block — new Tier-2 towers risky",
      "Bala Nagar low-lying lanes flooded 3 times in last 2 monsoons",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 64,
    tags: ["suburb", "family-friendly", "builder-variance"],
    sources: [
      { label: "Builder rating index (draft)" },
      { label: "Monsoon flood map 2024" },
    ],
  },
  {
    id: "manikonda",
    name: "Manikonda",
    coord: [78.3908, 17.4046],
    verdict: "green",
    reasons: [
      "8–12 min to Financial District via Khajaguda bridge",
      "Mid-tier towers from known builders; 3BHK stock plentiful",
      "Old AP-era title disputes on some plots; get legal vetting",
    ],
    priceRange: "₹7k–10k / sqft",
    confidenceScore: 72,
    tags: ["it-adjacent", "mid-rise", "legal-caution"],
    sources: [
      { label: "Title audit — sample n=12" },
      { label: "Broker sales velocity" },
    ],
    priceData: {
      buyApartmentMedian: 95,
      buyHouseMedian: 165,
      rentApartmentMedian: 30,
      rentHouseMedian: 48,
      source: "99acres Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "FD in 10 min — best price-to-commute for IT mid-ticket buyers",
        "3BHK inventory from Aparna, My Home sustains ₹95L entry",
      ],
      risks: [
        "AP-era title disputes on some plots — legal vetting essential",
        "Khajaguda bridge congestion spikes 6–9pm",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "nanakramguda",
    name: "Nanakramguda",
    coord: [78.3451, 17.4201],
    verdict: "green",
    reasons: [
      "Wipro, Microsoft campuses walkable; rents ~15% above Gachibowli",
      "ORR entry at Kollur — airport 30 min, HITEC 10 min off-peak",
      "Schools + hospitals still thin; expect 15-min drive for essentials",
    ],
    priceRange: "₹8k–11k / sqft",
    confidenceScore: 74,
    tags: ["it-corridor", "premium", "maturing"],
    sources: [
      { label: "Rental yield index" },
      { label: "Amenities density (OSM)" },
    ],
    priceData: {
      buyApartmentMedian: 145,
      buyHouseMedian: 275,
      rentApartmentMedian: 48,
      rentHouseMedian: 75,
      source: "99acres + rental yield index Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Microsoft, Wipro walkable — walk-to-work rent premium persists",
        "Yield 3.8–4.2% — best in HYD IT-corridor premium",
        "ORR Kollur junction + metro Phase II candidate — commute improvements priced-in",
      ],
      risks: [
        "Essentials (school, hospital) still 15-min drive — family buyers delay",
        "Infra maturity 3–4 years out — early movers take execution risk",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "tellapur",
    name: "Tellapur",
    coord: [78.2895, 17.4781],
    verdict: "amber",
    reasons: [
      "New launches every quarter; entry prices still under ₹7k/sqft",
      "Long commute: HITEC 30–45 min peak via narrow Tellapur Rd",
      "Multiple builders slipped handover 12–18 months in last 2 years",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 61,
    tags: ["growth", "supply-heavy", "handover-risk"],
    sources: [
      { label: "RERA filings 2024" },
      { label: "Forum thread scrape" },
    ],
    priceData: {
      buyApartmentMedian: 78,
      buyHouseMedian: 130,
      rentApartmentMedian: 24,
      rentHouseMedian: 38,
      source: "RERA filings 2024 + builder listings",
      year: 2024,
    },
    investorEvidence: {
      tier: "weaker",
      drivers: [
        "Under-₹7k/sqft entry — cheapest gated stock near IT corridor",
      ],
      risks: [
        "Handover slippage 12–18 months common 2022–24 — execution risk real",
        "Supply-heavy: 15+ towers in 2km radius — price pressure persistent",
        "Tellapur Rd bottleneck; no planned capacity upgrade",
      ],
      timeframe: "long",
    },
  },
  {
    id: "kokapet",
    name: "Kokapet",
    coord: [78.3321, 17.4105],
    verdict: "green",
    reasons: [
      "TSIIC auction plots sold ₹55–100 cr/acre since 2022 — price floor firm",
      "Next-gen FD corridor: Amazon, TCS, Micron towers under construction",
      "Entry ticket ₹2.5cr+; small resales stuck, liquidity thin below 1500 sqft",
    ],
    priceRange: "₹8k–12k / sqft",
    confidenceScore: 77,
    tags: ["premium", "future-fd", "capex-high"],
    sources: [
      { label: "TSIIC auction data" },
      { label: "Builder launch tracker" },
    ],
    priceData: {
      buyApartmentMedian: 195,
      buyHouseMedian: 380,
      rentApartmentMedian: 52,
      rentHouseMedian: 95,
      source: "TSIIC auction data + builder launch tracker 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "TSIIC land auctions set ₹55–100Cr/acre floor since 2022 — priced-in scarcity",
        "Amazon, TCS, Micron confirmed campuses under construction",
        "ORR + Nehru Outer Ring = airport 25 min, CBD 30 min",
      ],
      risks: [
        "Residential ecosystem 3–5 years behind jobs — early movers wait on schools/retail",
        "Entry ticket ₹2.5Cr+ skews buyer pool; small-ticket liquidity thin",
      ],
      timeframe: "long",
    },
  },

  // --- Central / premium ---
  {
    id: "banjara-hills",
    name: "Banjara Hills",
    coord: [78.4379, 17.4166],
    verdict: "green",
    reasons: [
      "Most mature premium pocket; prices flat-to-up ~4%/yr, rarely dip",
      "Apollo, KIMS, 40+ cafes and bars within 2 km — walkable lifestyle",
      "Rental yield 2.2–2.8% — ticket sizes ₹3cr+, slow liquidity below that",
    ],
    priceRange: "₹15k–25k / sqft",
    confidenceScore: 90,
    tags: ["premium", "mature-infra", "low-yield"],
    sources: [
      { label: "Amenities density (OSM)" },
      { label: "Broker price index 2025" },
    ],
    priceData: {
      buyApartmentMedian: 280,
      buyHouseMedian: 550,
      rentApartmentMedian: 70,
      rentHouseMedian: 120,
      source: "99acres + broker price index 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Legacy premium — rare liquidity events but ticket holds value",
        "Medical corridor (Apollo/KIMS) anchors long-term demand",
      ],
      risks: [
        "Yield 2.2–2.8% — capital parking, not cash flow",
        "Ticket ₹3Cr+ limits resale pool; liquidity thin below 1800 sqft",
        "Jubilee Hills + Kokapet have siphoned wealthy end-buyers since 2022",
      ],
      timeframe: "long",
    },
  },
  {
    id: "jubilee-hills",
    name: "Jubilee Hills",
    coord: [78.4106, 17.432],
    verdict: "green",
    reasons: [
      "Prestige address; independent-house resales cross ₹30cr regularly",
      "Tree cover 30%+, elevated terrain — no flooding recorded since 2020",
      "Maintenance ₹15–25/sqft — tall upkeep on older 4000+ sqft homes",
    ],
    priceRange: "₹18k–30k / sqft",
    confidenceScore: 92,
    tags: ["premium", "low-density", "legacy-elite"],
    sources: [
      { label: "GHMC flood map 2024" },
      { label: "Broker price index 2025" },
    ],
    priceData: {
      buyApartmentMedian: 360,
      buyHouseMedian: 850,
      rentApartmentMedian: 95,
      rentHouseMedian: 180,
      source: "Broker price index 2025 + sample listings (n=18)",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Irreplaceable low-density elevated land — scarcity protects floor",
        "Celebrity + HNI pull — no churn on top-end plots since 2020",
      ],
      risks: [
        "Yield 2.0–2.5% — worst cash flow in HYD premium tier",
        "Maintenance cost ₹15–25/sqft on older villas compresses net return",
      ],
      timeframe: "long",
    },
  },
  {
    id: "film-nagar",
    name: "Film Nagar",
    coord: [78.4025, 17.4142],
    verdict: "green",
    reasons: [
      "Hill-top elevation — dry even in October cloudbursts",
      "Aparna, Lodha, Rajapushpa gated stock; resale under 60 days typical",
      "Single-lane feeder roads clog 8–10am around school-run",
    ],
    priceRange: "₹12k–20k / sqft",
    confidenceScore: 79,
    tags: ["premium", "gated", "elevated"],
    sources: [
      { label: "HMDA drainage audit 2024" },
    ],
  },
  {
    id: "somajiguda",
    name: "Somajiguda",
    coord: [78.4611, 17.4248],
    verdict: "amber",
    reasons: [
      "Walkable to Raj Bhavan Rd corporate offices and Green Park",
      "Most buildings 20+ years old; budget ₹800/sqft for renovation",
      "Street parking full by 10am weekdays; traffic noise till 11pm",
    ],
    priceRange: "₹10k–15k / sqft",
    confidenceScore: 70,
    tags: ["cbd", "old-stock", "noise"],
    sources: [
      { label: "Building age survey (MCH)" },
      { label: "CPCB noise index" },
    ],
  },
  {
    id: "begumpet",
    name: "Begumpet",
    coord: [78.4673, 17.4436],
    verdict: "amber",
    reasons: [
      "Blue-line metro + MMTS; airport in 35 min via PVNR",
      "Flight noise 65–75dB near Begumpet airfield — light-sleepers beware",
      "Mixed-age stock — 1990s walk-ups next to 2022 towers on same lane",
    ],
    priceRange: "₹9k–13k / sqft",
    confidenceScore: 66,
    tags: ["transit-hub", "noise", "mixed-age"],
    sources: [
      { label: "AAI flight path data" },
      { label: "CPCB noise index" },
    ],
  },
  {
    id: "ameerpet",
    name: "Ameerpet",
    coord: [78.4469, 17.4375],
    verdict: "amber",
    reasons: [
      "Three-line metro interchange; 18 min to Miyapur, 20 min to LB Nagar",
      "50+ coaching institutes — 7am–10pm foot traffic is relentless",
      "Parking chaos — most old buildings have zero setback; dusty lanes",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 74,
    tags: ["transit-hub", "dense", "coaching"],
    sources: [
      { label: "Metro daily ridership" },
      { label: "Footfall sample 2025" },
    ],
  },
  {
    id: "punjagutta",
    name: "Punjagutta",
    coord: [78.4518, 17.4272],
    verdict: "amber",
    reasons: [
      "Walkable to Khairatabad, Somajiguda offices; Hyderabad Central mall on foot",
      "Punjagutta flyover junction locks 6–9pm daily — allow +20 min",
      "Mixed-zone noise: shops shutter 10pm, honking till midnight",
    ],
    priceRange: "₹9k–13k / sqft",
    confidenceScore: 71,
    tags: ["central", "retail", "traffic-pressure"],
    sources: [
      { label: "Traffic pulse — T-HITS 2025" },
      { label: "Retail density (OSM)" },
    ],
  },

  // --- Old city / south ---
  {
    id: "charminar",
    name: "Charminar",
    coord: [78.4747, 17.3616],
    verdict: "red",
    reasons: [
      "400-year heritage core; boutique hotels paying premium for street-facing units",
      "Severe congestion — 8ft lanes, no setbacks, ambulance access tight",
      "Legacy plumbing + 80-year title chains — legal vetting essential",
    ],
    priceRange: "₹4k–7k / sqft",
    confidenceScore: 83,
    tags: ["heritage", "congested", "utility-stress"],
    sources: [
      { label: "GHMC heritage zone map" },
      { label: "Title audit — sample n=8" },
    ],
  },
  {
    id: "lb-nagar",
    name: "LB Nagar",
    coord: [78.5557, 17.3464],
    verdict: "amber",
    reasons: [
      "Metro terminus; 30 min to Ameerpet, 50 min to Miyapur",
      "Sagar Rd junction peak-congestion — 4 signals in 2 km",
      "₹5–7k/sqft; 2BHK resale 900–1100 sqft for ~₹55L — entry-home sweet spot",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 69,
    tags: ["transit-hub", "affordable", "suburb"],
    sources: [
      { label: "Metro daily ridership" },
      { label: "Traffic pulse — T-HITS 2025" },
    ],
  },
  {
    id: "dilsukhnagar",
    name: "Dilsukhnagar",
    coord: [78.5261, 17.3687],
    verdict: "amber",
    reasons: [
      "Coaching capital — 1BHK student rentals ₹12–18k, near-zero vacancy",
      "Saroornagar Rd lanes flood knee-deep 2–3 times each monsoon",
      "Bus station PM2.5 stays elevated till 11am — keep windows closed early",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 71,
    tags: ["retail", "students", "flood-risk"],
    sources: [
      { label: "GHMC flood map 2024" },
      { label: "CPCB air data 2024" },
    ],
  },
  {
    id: "abids",
    name: "Abids",
    coord: [78.4741, 17.3886],
    verdict: "amber",
    reasons: [
      "GPO + Sunday book-market; walkable to Nampally, Khairatabad",
      "Most stock built pre-1990; lift upgrades + wiring rework likely",
      "Parking impossible Mon–Sat 10am–8pm — tow-risk on main roads",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 67,
    tags: ["central", "commercial", "old-stock"],
    sources: [
      { label: "Building age survey (MCH)" },
    ],
  },
  {
    id: "koti",
    name: "Koti",
    coord: [78.4807, 17.3833],
    verdict: "red",
    reasons: [
      "Legacy wholesale markets; fabric + electronics hubs 50+ years old",
      "Dense footprint blocks daylight — many units see <2 hrs direct sun",
      "Title chains run 3+ generations — mixed safety on night walks near junction",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 73,
    tags: ["commercial", "legacy", "legal-caution"],
    sources: [
      { label: "Title audit — sample n=6" },
      { label: "CPCB noise index" },
    ],
  },
  {
    id: "malakpet",
    name: "Malakpet",
    coord: [78.5009, 17.3776],
    verdict: "amber",
    reasons: [
      "2BHK under ₹50L possible; 15 min to Abids, 25 min to HITEC off-peak",
      "Race-course event days gridlock 5pm–11pm, Nov–Feb",
      "Drainage upgrade ongoing since 2023 — inner lanes still dug up in patches",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 58,
    tags: ["affordable", "city-centre", "infra-pending"],
    sources: [{ label: "GHMC works tracker 2025" }],
  },

  // --- North ---
  {
    id: "kompally",
    name: "Kompally",
    coord: [78.4881, 17.5381],
    verdict: "amber",
    reasons: [
      "New gated-community belt; Aparna, My Home, Sumadhura launches active",
      "Long commute: Begumpet 45 min peak, HITEC 60+ min via Balanagar",
      "Oakridge + Delhi Public School cluster — water tankers run Apr–Jun",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 63,
    tags: ["outer", "growth", "water-check"],
    sources: [
      { label: "HMWSSB water index 2024" },
      { label: "Commute time, Google distance matrix" },
    ],
  },
  {
    id: "alwal",
    name: "Alwal",
    coord: [78.506, 17.502],
    verdict: "amber",
    reasons: [
      "Three-decade middle-class suburb; 1200-sqft flats under ₹55L currently",
      "Mix of 1990s independents and 2020+ towers within same block",
      "Venkatapuram & Yapral pockets flood Aug monsoon — check ground-floor units",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 62,
    tags: ["suburb", "middle-class", "drainage-risk"],
    sources: [
      { label: "HMDA drainage audit 2024" },
    ],
  },
  {
    id: "bowenpally",
    name: "Bowenpally",
    coord: [78.4802, 17.478],
    verdict: "amber",
    reasons: [
      "Cantonment governance — road quality above GHMC average",
      "Bowenpally wholesale veg market clogs 5–10am daily",
      "Inner lanes dust + noise from Balanagar trucks till 11pm",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 60,
    tags: ["cantonment", "market", "noise"],
    sources: [{ label: "CPCB noise index" }],
  },
  {
    id: "sainikpuri",
    name: "Sainikpuri",
    coord: [78.5518, 17.5065],
    verdict: "green",
    reasons: [
      "Defence-layout 40ft roads; tree cover mature, power lines buried",
      "Crime index among lowest in GHMC; dog-walk streets till 10pm",
      "Long commute: HITEC 75–90 min peak; freeway-dependent",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 75,
    tags: ["defence", "low-crime", "commute-long"],
    sources: [
      { label: "Crime index, TS Police" },
    ],
  },
  {
    id: "secunderabad",
    name: "Secunderabad",
    coord: [78.5014, 17.4399],
    verdict: "amber",
    reasons: [
      "Junction rail hub — direct trains to Bangalore, Mumbai, Vizag",
      "Mixed heritage + commercial zoning; cantonment land-use rules vary lane-to-lane",
      "Paradise junction ~30-min crawl 6–10pm — allow buffer for pickups",
    ],
    priceRange: "₹8k–12k / sqft",
    confidenceScore: 78,
    tags: ["rail-hub", "mixed-zoning", "traffic-pressure"],
    sources: [
      { label: "Railway footfall data" },
      { label: "Traffic pulse — T-HITS 2025" },
    ],
  },
  {
    id: "trimulgherry",
    name: "Trimulgherry",
    coord: [78.5035, 17.4862],
    verdict: "amber",
    reasons: [
      "Cantonment-zone quiet; streetlights and policing consistent after dark",
      "Setback + height rules stricter — redev approvals take 12–18 months",
      "New cafes/retail rare; groceries + schools fine, nightlife non-existent",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 67,
    tags: ["cantonment", "low-crime", "slow-growth"],
    sources: [
      { label: "Crime index, TS Police" },
    ],
  },

  // --- East ---
  {
    id: "uppal",
    name: "Uppal",
    coord: [78.5593, 17.4057],
    verdict: "amber",
    reasons: [
      "Metro end + ORR entry in 2 km; Secunderabad 25 min off-peak",
      "Rajiv Gandhi stadium match days lock lanes for 3–4 hrs",
      "Nacharam industrial backs east; winter AQI often 'poor' category",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 65,
    tags: ["transit-hub", "industrial-edge", "affordable"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "Event-day traffic logs" },
    ],
  },
  {
    id: "habsiguda",
    name: "Habsiguda",
    coord: [78.5484, 17.4062],
    verdict: "amber",
    reasons: [
      "5 min to Osmania University; DRDO + NGRI scientist families long-settled",
      "Most stock 20–30 years old; redev stuck on mixed-share title plots",
      "Schools decent, hospitals mixed — Apollo 15 min away for anything serious",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 66,
    tags: ["academic", "old-stock", "redev-uneven"],
    sources: [
      { label: "Building age survey (MCH)" },
    ],
  },
  {
    id: "nagole",
    name: "Nagole",
    coord: [78.559, 17.3765],
    verdict: "amber",
    reasons: [
      "Metro + ORR exit 3; Uppal 10 min, airport 40 min via ORR",
      "Musi river within 500m — lower plots flood during peak release",
      "Entry ₹4–6k/sqft; new Aparna + Rajapushpa towers since 2023",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 60,
    tags: ["transit-hub", "river-risk", "affordable"],
    sources: [
      { label: "GHMC flood map 2024" },
      { label: "Metro daily ridership" },
    ],
  },
  {
    id: "ghatkesar",
    name: "Ghatkesar",
    coord: [78.6815, 17.4429],
    verdict: "red",
    reasons: [
      "Long commute: HITEC 75–90 min even off-peak; NH-163 freeway-dependent",
      "Industrial belt — factory effluents into Musi; groundwater unfit to drink",
      "Upside hinges on Regional Ring Road — still 3–4 years out",
    ],
    priceRange: "₹3k–5k / sqft",
    confidenceScore: 63,
    tags: ["far-east", "industrial", "speculative"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "HMWSSB water index 2024" },
    ],
  },

  // --- Outer / ORR belt ---
  {
    id: "shamshabad",
    name: "Shamshabad",
    coord: [78.4011, 17.2403],
    verdict: "amber",
    reasons: [
      "RGIA airport 10 min; Amazon + DTDC warehousing employs locally",
      "Residential ecosystem still thin — no malls, basic K-12 only",
      "Plots up 20–25% in 2 yrs; rental demand weak, 3–4% vacancy typical",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 64,
    tags: ["airport", "logistics", "thin-residential"],
    sources: [
      { label: "AAI traffic data" },
      { label: "Rental yield index" },
    ],
  },
  {
    id: "shamirpet",
    name: "Shamirpet",
    coord: [78.5714, 17.5933],
    verdict: "amber",
    reasons: [
      "Genome Valley pharma hub — Dr Reddy, Bharat Biotech within 10 km",
      "Shamirpet lake edge — FTL setbacks enforced, confirm plot boundary",
      "Long commute: Secunderabad 50–70 min peak via narrow ORR arm",
    ],
    priceRange: "₹3k–5k / sqft",
    confidenceScore: 58,
    tags: ["pharma", "lake", "commute-long"],
    sources: [
      { label: "TSIIC corridor map" },
    ],
  },
  {
    id: "pocharam",
    name: "Pocharam",
    coord: [78.6332, 17.4096],
    verdict: "amber",
    reasons: [
      "Infosys SEZ + TCS Adibatla hiring — 2BHK rentals up ~10% last 6 months",
      "New CBSE schools opened 2024; basics improving, still thin on retail",
      "Early buyers flagged 12–24 month handover slippage — due-diligence the builder",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 61,
    tags: ["sez", "growth", "handover-risk"],
    sources: [
      { label: "RERA filings 2024" },
    ],
  },
  {
    id: "adibatla",
    name: "Adibatla",
    coord: [78.5949, 17.2407],
    verdict: "amber",
    reasons: [
      "DRDL + TCS Adibatla aerospace cluster; stable govt-backed tenant pool",
      "Prices up modestly (~6%/yr) — not speculative, driven by real demand",
      "Far south — 45 min to LB Nagar, 60+ to HITEC; freeway-dependent",
    ],
    priceRange: "₹3k–5k / sqft",
    confidenceScore: 59,
    tags: ["defence", "aerospace", "far-south"],
    sources: [
      { label: "TSIIC corridor map" },
    ],
  },
  {
    id: "patancheru",
    name: "Patancheru",
    coord: [78.2635, 17.5313],
    verdict: "amber",
    reasons: [
      "IDA Patancheru jobs nearby; factories employ 40,000+",
      "Long-running pollution — CPCB listed red-category since 1990s",
      "₹3–5k/sqft entry; groundwater contaminated, insist on Metro Water supply",
    ],
    priceRange: "₹3k–5k / sqft",
    confidenceScore: 72,
    tags: ["industrial", "environmental-risk", "affordable"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "HMWSSB water index 2024" },
      { label: "TSPCB industrial audit 2024" },
    ],
  },
  {
    id: "bachupally",
    name: "Bachupally",
    coord: [78.3571, 17.5226],
    verdict: "green",
    reasons: [
      "Quiet residential pocket; resales moving in 45–60 days currently",
      "Narayana, Kendriya + Ankura clinic opened in last 18 months",
      "Elevated pockets dry; lower stretches toward Pragathi Nagar pond risky in monsoon",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 68,
    tags: ["suburb", "maturing", "low-flood"],
    sources: [
      { label: "GHMC flood map 2024" },
      { label: "Amenities density (OSM)" },
    ],
  },
  {
    id: "narsingi",
    name: "Narsingi",
    coord: [78.3504, 17.3884],
    verdict: "green",
    reasons: [
      "ORR entry in 2 min; FD in 10 min off-peak, 20 min peak",
      "Lodha, My Home, Prestige premium towers launched 2022–24",
      "Narsingi Rd upgrade done 2024 — AM congestion eased noticeably",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 70,
    tags: ["it-adjacent", "premium", "maturing"],
    sources: [
      { label: "Traffic pulse — T-HITS 2025" },
      { label: "Broker launch tracker" },
    ],
    priceData: {
      buyApartmentMedian: 105,
      buyHouseMedian: 195,
      rentApartmentMedian: 34,
      rentHouseMedian: 58,
      source: "Broker launch tracker 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Lodha + My Home + Prestige launched post-2022 — Tier-1 builder signal",
        "ORR + FD connectivity priced-in — Narsingi Rd upgrade (2024) already paying",
        "Still under ₹1.1Cr for new 2BHK — catching up to Kondapur/Manikonda",
      ],
      risks: [
        "Supply-heavy — 6+ new launches 2022–24 absorbing into same corridor",
        "Yields thin (3.0–3.4%) — buyers paying for future, not current cash flow",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "financial-district",
    name: "Financial District",
    coord: [78.3475, 17.4125],
    verdict: "green",
    reasons: [
      "Deutsche, JPMC, Wells Fargo anchor tenants — weekday foot traffic huge",
      "Metro + ORR + Nehru ORR — airport 25 min, CBD 30 min",
      "Entry ticket ₹2.5cr+; 1BHKs almost absent — inventory skews 3BHK+",
    ],
    priceRange: "₹10k–14k / sqft",
    confidenceScore: 85,
    tags: ["bfsi", "premium", "mature-infra"],
    sources: [
      { label: "Office absorption — broker report" },
      { label: "Metro daily ridership" },
    ],
    priceData: {
      buyApartmentMedian: 175,
      buyHouseMedian: 340,
      rentApartmentMedian: 55,
      rentHouseMedian: 100,
      source: "Office absorption + broker panel Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Deutsche + JPMC + Wells Fargo absorbed 4.2M sqft office 2022–24",
        "Metro Phase II stop confirmed — connects airport + CBD one-seat",
        "3BHK rents ₹55–75k stable; BFSI tenant pool doesn't churn",
      ],
      risks: [
        "1BHK stock minimal — single tenant demographic (family BFSI)",
        "Entry ticket ₹2.5Cr+ — small-buyer cut off from the yield story",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "gandipet",
    name: "Gandipet",
    coord: [78.33, 17.385],
    verdict: "green",
    reasons: [
      "Osman Sagar reservoir + tree cover; temp runs 2–3°C cooler in summer",
      "Farmhouse belt — 1-acre plots from ₹4–8cr; weekend-home crowd",
      "Utilities patchy inside — borewell + diesel gensets common in interior plots",
    ],
    priceRange: "₹7k–10k / sqft",
    confidenceScore: 66,
    tags: ["low-density", "farmhouse", "utility-patchy"],
    sources: [
      { label: "HMWSSB water index 2024" },
    ],
  },
  {
    id: "attapur",
    name: "Attapur",
    coord: [78.4263, 17.3728],
    verdict: "amber",
    reasons: [
      "PVNR expressway on-ramp; airport 25 min, HITEC 30 min off-peak",
      "Dense low-rise; quality swings — Tier-1 builders rare below ₹6k/sqft",
      "Monsoon flooding on Rajendra Nagar Rd approach — check feeder-lane elevation",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 60,
    tags: ["expressway", "low-rise", "drainage-risk"],
    sources: [
      { label: "HMDA drainage audit 2024" },
    ],
  },
  {
    id: "mehdipatnam",
    name: "Mehdipatnam",
    coord: [78.4396, 17.3957],
    verdict: "amber",
    reasons: [
      "MMTS + bus terminus — Gachibowli, Charminar reachable under 30 min off-peak",
      "Ring-road junction: heavy traffic, dust on inner lanes throughout weekday",
      "2BHK rents ₹18–25k near-zero vacancy; students + pg demand stable",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 72,
    tags: ["transit-hub", "dusty", "affordable"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "Rental yield index" },
    ],
  },
  {
    id: "rajendranagar",
    name: "Rajendranagar",
    coord: [78.4094, 17.3171],
    verdict: "amber",
    reasons: [
      "Airport 15 min via PVNR; Outer Ring Rd in 5 min",
      "Mixed residential + agri zoning — land-use conversion risk on older plots",
      "Water + power uneven inside; borewell + backup needed in most independents",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 58,
    tags: ["airport", "mixed-zoning", "utility-uneven"],
    sources: [
      { label: "HMWSSB water index 2024" },
    ],
  },
  {
    id: "khajaguda",
    name: "Khajaguda",
    coord: [78.3852, 17.4183],
    verdict: "green",
    reasons: [
      "FD in 8 min via Nanakramguda bridge; density ~40% below Madhapur",
      "Hill elevation — no flooding reports in last 5 monsoons",
      "New 2024 launches offer 2-car parking standard; older stock often 1-car",
    ],
    priceRange: "₹7k–10k / sqft",
    confidenceScore: 71,
    tags: ["it-adjacent", "elevated", "low-density"],
    sources: [
      { label: "HMDA drainage audit 2024" },
      { label: "Broker launch tracker" },
    ],
  },
  {
    id: "tolichowki",
    name: "Tolichowki",
    coord: [78.4071, 17.4003],
    verdict: "amber",
    reasons: [
      "10 min to Jubilee, 15 to HITEC; 24-hr kebab + bakery scene on main road",
      "Main artery noisy till midnight Fri–Sat; honking steady through evening",
      "Long-settled community; narrow inner lanes mean poor ventilation in flats",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 67,
    tags: ["retail", "dense", "community"],
    sources: [
      { label: "CPCB noise index" },
    ],
  },
  {
    id: "serilingampally",
    name: "Serilingampally",
    coord: [78.3186, 17.4951],
    verdict: "amber",
    reasons: [
      "Between Kondapur and Miyapur — HITEC 15 min, Tellapur 10 min",
      "Oakridge, DPS, CARE hospital opened since 2022",
      "GHMC drainage upgrade still in progress; some lanes dug up through 2026",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 64,
    tags: ["it-bridge", "maturing", "drainage-upgrade"],
    sources: [
      { label: "GHMC works tracker 2025" },
    ],
  },
  {
    id: "chandanagar",
    name: "Chandanagar",
    coord: [78.3292, 17.4946],
    verdict: "amber",
    reasons: [
      "20+ schools in 3 km; Sri Chaitanya, Narayana, DPS all within walking",
      "NH-65 traffic 65–70dB till 11pm — west-facing lower floors hear it constantly",
      "₹4–6k/sqft, HITEC 25 min off-peak, 45 min peak — price-commute trade-off",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 66,
    tags: ["family-friendly", "highway-noise", "affordable"],
    sources: [
      { label: "CPCB noise index" },
      { label: "Commute time, Google distance matrix" },
    ],
  },
  {
    id: "puppalaguda",
    name: "Puppalaguda",
    coord: [78.3674, 17.403],
    verdict: "green",
    reasons: [
      "Between Manikonda and Narsingi; FD 10 min, HITEC 15 min off-peak",
      "~90% gated tower stock post-2018; few independents to worry about",
      "Puppalaguda Rd congested 9–10am school-run; otherwise flows",
    ],
    priceRange: "₹7k–10k / sqft",
    confidenceScore: 70,
    tags: ["it-adjacent", "gated", "roads-upgraded"],
    sources: [
      { label: "Broker launch tracker" },
    ],
  },

  // --- New in v2 (10 more) ---
  {
    id: "yousufguda",
    name: "Yousufguda",
    coord: [78.4348, 17.4464],
    verdict: "green",
    reasons: [
      "Jubilee Hills edge — lifestyle same, prices ~30% lower",
      "Delhi Public School + Kodi Eatery walk-score high; cafes opening monthly",
      "Narrow inner lanes clog 8:30–9:30am entering Road No 45",
    ],
    priceRange: "₹8k–12k / sqft",
    confidenceScore: 69,
    tags: ["quiet", "premium-adjacent", "walkable"],
    sources: [
    ],
  },
  {
    id: "erragadda",
    name: "Erragadda",
    coord: [78.4397, 17.4587],
    verdict: "amber",
    reasons: [
      "Green-line metro + ESI, NIMS hospitals in 2 km",
      "SR Nagar–Erragadda Rd dust + honking heavy through weekday",
      "Most flats 15–25 years old — budget ₹500–800/sqft for lift/plumbing rework",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 65,
    tags: ["transit-hub", "dusty", "old-stock"],
    sources: [
      { label: "CPCB air data 2024" },
      { label: "Building age survey (MCH)" },
    ],
  },
  {
    id: "musheerabad",
    name: "Musheerabad",
    coord: [78.502, 17.4163],
    verdict: "amber",
    reasons: [
      "Secunderabad 10 min, RTC X-Roads 5 min; metro + bus at doorstep",
      "RTC X-Roads junction locks 7–10pm; honking constant 7am–11pm",
      "Most stock pre-2005; power-load + water-pressure drops summer afternoons",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 63,
    tags: ["central", "dense", "old-stock"],
    sources: [
      { label: "CPCB noise index" },
      { label: "Building age survey (MCH)" },
    ],
  },
  {
    id: "balanagar",
    name: "Balanagar",
    coord: [78.4399, 17.4765],
    verdict: "amber",
    reasons: [
      "IDA Balanagar jobs; NH-44 + inner ring for freight-adjacent renters",
      "2BHK under ₹45L; rents lag IT corridor but vacancy near zero",
      "Factory truck noise + dust till 11pm; AQI 30–40% worse than west IT",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 62,
    tags: ["industrial", "affordable", "environmental-caution"],
    sources: [
      { label: "TSPCB industrial audit 2024" },
    ],
  },
  {
    id: "quthbullapur",
    name: "Quthbullapur",
    coord: [78.4692, 17.5093],
    verdict: "amber",
    reasons: [
      "Northern growth belt; fresh launches along Medchal Road since 2023",
      "₹4–6k/sqft entry — 3BHK towers under ₹80L still available",
      "Long commute: HITEC 40–60 min peak; freeway-dependent to NH-44",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 60,
    tags: ["growth", "affordable", "commute-long"],
    sources: [
      { label: "Commute time, Google distance matrix" },
    ],
  },
  {
    id: "kapra",
    name: "Kapra",
    coord: [78.5776, 17.4852],
    verdict: "amber",
    reasons: [
      "Quiet NE suburb; tree-lined defence-planned streets",
      "ECIL + HMT jobs nearby; 2BHK under ₹50L, rents ₹15–20k stable",
      "Long commute to west IT (60–80 min peak); schools plentiful locally",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 58,
    tags: ["suburb", "affordable", "schools"],
    sources: [
      { label: "Amenities density (OSM)" },
    ],
  },
  {
    id: "malkajgiri",
    name: "Malkajgiri",
    coord: [78.5361, 17.4435],
    verdict: "amber",
    reasons: [
      "MMTS station; 20 min direct to Begumpet, 35 min to Secunderabad",
      "2BHK ₹50–65L; weekly market + dense retail keep rentals sticky",
      "Station-lane traffic + horn noise 6–10pm daily; auto stand dust pocket",
    ],
    priceRange: "₹5k–7k / sqft",
    confidenceScore: 64,
    tags: ["rail-hub", "retail", "noise"],
    sources: [
      { label: "Railway footfall data" },
      { label: "CPCB noise index" },
    ],
  },
  {
    id: "ramanthapur",
    name: "Ramanthapur",
    coord: [78.5505, 17.4039],
    verdict: "amber",
    reasons: [
      "Stable middle-class east — 2BHK ₹45–55L, turnover modest but steady",
      "GHMC drainage overhaul 2024 eased lane flooding noticeably",
      "NGRI Rd + feeder lanes clog 8–10am with Uppal-Peerzadiguda commuters",
    ],
    priceRange: "₹4k–6k / sqft",
    confidenceScore: 61,
    tags: ["suburb", "middle-class", "drainage-improving"],
    sources: [
      { label: "HMDA drainage audit 2024" },
    ],
  },
  {
    id: "santoshnagar",
    name: "Santoshnagar",
    coord: [78.494, 17.3577],
    verdict: "red",
    reasons: [
      "Old-city south fringe — ~5 km to Charminar, 10 to Abids",
      "Peak congestion through Chandrayangutta till 10pm; pre-1995 wiring/plumbing common",
      "₹3–5k/sqft; resales sit 6–12 months, liquidity thin",
    ],
    priceRange: "₹3k–5k / sqft",
    confidenceScore: 66,
    tags: ["old-city", "congested", "low-liquidity"],
    sources: [
      { label: "Broker sales velocity index" },
      { label: "GHMC works tracker 2025" },
    ],
  },
  {
    id: "shaikpet",
    name: "Shaikpet",
    coord: [78.4103, 17.4076],
    verdict: "green",
    reasons: [
      "Jubilee Hills + Manikonda on either side; walk to Durgam Cheruvu deck",
      "Mostly 2018+ gated towers; 3BHK fresh resale in 30–45 days",
      "Shaikpet Rd narrow — school-run plus Salar Jung traffic stacks 8–10am",
    ],
    priceRange: "₹9k–13k / sqft",
    confidenceScore: 72,
    tags: ["premium-adjacent", "gated", "low-density"],
    sources: [
      { label: "Broker launch tracker" },
      { label: "Amenities density (OSM)" },
    ],
  },
];

/**
 * Public HYD verdicts array — same records as `_HYD_RAW`, with
 * `priceData` + `investorEvidence` back-filled from the pricing bank for
 * every suburb that did not define them inline.
 */
export const HYD_VERDICTS: Verdict[] = _HYD_RAW.map((v) => {
  // getPricingFor applies the monthly override on top of the static bank.
  // Inline priceData on a raw verdict still takes precedence via `??`.
  const bank = getPricingFor(v.id);
  if (!bank) return v;
  return {
    ...v,
    priceData: v.priceData ?? bank.priceData,
    investorEvidence: v.investorEvidence ?? bank.investorEvidence,
  };
});
// Suppress lint error for the HYD_SUBURB_PRICING import — still re-exported.
void HYD_SUBURB_PRICING;

/** City default center (map initial focus). */
export const HYD_CENTER: [number, number] = [78.4867, 17.385];

/**
 * Find the nearest verdict location to a clicked point.
 * Returns null if nothing is within `maxKm`.
 */
export function nearestVerdict(
  lng: number,
  lat: number,
  maxKm = 3
): Verdict | null {
  let best: Verdict | null = null;
  let bestKm = Infinity;
  for (const v of HYD_VERDICTS) {
    const km = haversineKm(lat, lng, v.coord[1], v.coord[0]);
    if (km < bestKm) {
      bestKm = km;
      best = v;
    }
  }
  return bestKm <= maxKm ? best : null;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
